import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Mail, X } from 'lucide-react';
import { toast } from 'sonner';
import { translateAuthError } from '@/lib/authErrors';

/**
 * Aviso não bloqueante de e-mail pendente de confirmação.
 * Some automaticamente quando o usuário volta pelo link (?email_confirmed=1).
 */
export const EmailConfirmationBanner = () => {
  const [pending, setPending] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let active = true;

    const run = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !active) {
        setPending(false);
        return;
      }
      setEmail(user.email || '');

      const confirmed = new URLSearchParams(location.search).get('email_confirmed') === '1';
      if (confirmed) {
        await supabase.from('profiles').update({ email_confirmation_pending: false }).eq('id', user.id);
        if (active) setPending(false);
        return;
      }

      const { data } = await supabase
        .from('profiles')
        .select('email_confirmation_pending')
        .eq('id', user.id)
        .maybeSingle();

      if (active) setPending(Boolean(data?.email_confirmation_pending));
    };

    run();
    return () => { active = false; };
  }, [location.search, location.pathname]);

  if (!pending || dismissed) return null;

  const resend = async () => {
    setSending(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: `${window.location.origin}/?email_confirmed=1`,
        },
      });
      if (error) throw error;
      toast.success('Link de confirmação reenviado. Verifique sua caixa de entrada e o spam.');
    } catch (err) {
      toast.error(translateAuthError(err, 'Não foi possível reenviar o e-mail agora.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="w-full bg-primary/10 border-b border-primary/20">
      <div className="container mx-auto px-4 py-2 flex items-center gap-3 text-sm">
        <Mail className="h-4 w-4 text-primary shrink-0" />
        <span className="flex-1 text-foreground">
          Confirme seu e-mail <span className="text-muted-foreground">({email})</span> para garantir o acesso à sua conta.
        </span>
        <Button size="sm" variant="outline" onClick={resend} disabled={sending}>
          {sending ? 'Enviando...' : 'Reenviar'}
        </Button>
        <button
          type="button"
          aria-label="Fechar aviso"
          onClick={() => setDismissed(true)}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
